import { Stripe } from '@stripe/stripe-js';
import ApiRequest from '../../libs/ApiRequest';
import { store, line_items_key } from '../../constants/globals';
import { PaymentIntentPayload } from './StripeCheckoutForm';
import { isErrorResponse, isRequiresActionResp, InvoiceApiResp } from '../../types/json_types';
import { processInvoiceJSON } from '../../helpers/invoice_helpers';

const { setPartialState: setStripeForm } = store.getHelpers('stripe_form');

const handleInvoicePaid = (json: InvoiceApiResp): void => {
  const invoice = processInvoiceJSON(json.base.invoice, json.base.reservation);

  window.App.calcResp = json.calc;

  localStorage.removeItem(line_items_key);
  store.setState('invoice', invoice);
};

const handlePaymentError = (json: unknown): void => {
  if (isErrorResponse(json)) {
    alert(`The following errors occurred: ${json.errors.join(' & ')}`);
  } else {
    alert('an error occurred while trying to make the purchase');
  }
};

const confirmPayment = (invoice_id: number, payment_intent_id: string): void => {
  const req = new ApiRequest(
    '/store_api/confirm_payment.json',
    'POST',
    {
      onSuccess: (resp: unknown): void => {
        handleInvoicePaid(resp as InvoiceApiResp);
      },
      onError: handlePaymentError,
      onDone: (): void => {
        setStripeForm({ processing: false });
      },
    },
    {
      invoice_id,
      payment_intent_id,
    },
  );

  req.perform();
};

const handleRequiresAction = async (stripe: Stripe, client_secret: string, invoice_id: number): Promise<void> => {
  const result = await stripe.confirmCardPayment(client_secret);

  if (result.error || !result.paymentIntent || result.paymentIntent.status !== 'succeeded') {
    setStripeForm({
      error: result.error?.message || 'Card authentication failed. Please try again.',
      processing: false,
    });
  } else {
    // money has moved; the server verifies the intent with Stripe and marks
    // the invoice paid
    confirmPayment(invoice_id, result.paymentIntent.id);
  }
};

const handleStripeSubmit = async (
  stripe: Stripe,
  createPaymentIntent: () => Promise<PaymentIntentPayload>,
  cardComplete: boolean,
): Promise<void> => {
  if (cardComplete) {
    setStripeForm({ processing: true });
  }

  const payload = await createPaymentIntent();

  if (payload.error) {
    setStripeForm({
      error: payload.error.message,
      processing: false,
    });
  } else {
    const invoice = store.getNonNullState('invoice');

    const req = new ApiRequest(
      '/store_api/pay_invoice.json',
      'POST',
      {
        onSuccess: (resp: unknown): void => {
          if (isRequiresActionResp(resp)) {
            // keep the spinner going while the customer completes 3D Secure
            void handleRequiresAction(stripe, resp.client_secret, invoice.id);
          } else {
            handleInvoicePaid(resp as InvoiceApiResp);
            setStripeForm({ processing: false });
          }
        },
        onError: (json: unknown): void => {
          handlePaymentError(json);
          setStripeForm({ processing: false });
        },
      },
      {
        payment_method_token: payload?.paymentMethod?.id,
        invoice_id: invoice.id,
      },
    );

    req.perform();
  }
};

export default handleStripeSubmit;
